import { RouteComponentProps, withRouter } from 'react-router';

import * as patronUtils from '@tock/shared/utils/patron';
import { Button, Typography } from 'omakase';
import { DisplayDateTime } from '@tock/time';
import { IconCalendar, IconMapMarker, IconPerson } from '@tock/icons';
import { core, safeToNumber } from '@tock/coretypes';
import { getDiner } from '@tock/shared/utils/dinerUtils';
import { pluralize } from '@tock/shared/utils/pluralize';
import Anchor from 'components/Anchor';
import BaseComponent from '@tock/shared/components/BaseComponent';
import ButtonGroup from '@tock/shared/components/buttons/ButtonGroup';

interface Props {
  item: core.PastPurchase;
  // Whether to display booking actions. Valid only on bookings in the future.
  showBookingActions: boolean;
}

/**
 * Top level summary view of a purchase.
 */
class BookingHistoryItem extends BaseComponent<Props & RouteComponentProps> {
  getReservationsPath(): string | undefined {
    const { item } = this.props;
    if (item.id) {
      return `/profile/reservations/${safeToNumber(item.id)}`;
    }
    return undefined;
  }

  render() {
    const { item } = this.props;

    if (!item.latestPurchase) {
      return null;
    }

    const diner = getDiner(item);
    const owner = item.ownerPatron;
    const showGuestAndOwner = diner && owner && safeToNumber(diner.id) !== safeToNumber(owner.id);

    const partySize = item.ticketCount;

    return (
      <div className="BookingHistory-item">
        <Anchor href={this.getReservationsPath()} className="BookingHistory-itemDetails">
          <div className="BookingHistory-itemHeading">
            <Typography color="inherit" component="span" variant="heading03">
              {item.ticketTypeName}
            </Typography>
          </div>

          <div className="BookingHistory-itemDetail">
            <div className="Consumer-reservationMetaItemIcon">
              <IconMapMarker />
            </div>

            <div className="BookingHistory-itemDetailText">
              <Typography color="inherit" component="span" variant="body02">
                {item?.business?.name}
              </Typography>
            </div>
          </div>

          <div className="BookingHistory-itemDetail">
            <div className="Consumer-reservationMetaItemIcon">
              <IconPerson />
            </div>

            <div className="BookingHistory-itemDetailText">
              <Typography color="inherit" component="span" variant="body02">
                Reservation for {pluralize(partySize, 'guest')}
              </Typography>
            </div>
          </div>

          <div className="BookingHistory-itemDetail">
            <div className="Consumer-reservationMetaItemIcon">
              <IconCalendar />
            </div>
            <div className="BookingHistory-itemDetailText">
              <Typography color="inherit" component="span" variant="body02">
                <DisplayDateTime
                  dateTimeValue={item.ticketDateTime}
                  displayMonthAs="numeric"
                  showWeekday={true}
                />
              </Typography>
            </div>
          </div>
          {showGuestAndOwner && (
            <>
              <div className="BookingHistory-itemDetailDivider" />
              <div className="BookingHistory-itemDetail">
                <div className="BookingHistory-itemDetailText">
                  <Typography color="inherit" component="span" variant="body02">
                    Owner: {patronUtils.getFullName(owner)}
                  </Typography>
                </div>
              </div>
              <div className="BookingHistory-itemDetail">
                <div className="BookingHistory-itemDetailText">
                  <Typography color="inherit" component="span" variant="body02">
                    Diner: {`${patronUtils.getFullName(diner)}`}
                  </Typography>
                </div>
              </div>
            </>
          )}
        </Anchor>

        <div className="BookingHistory-itemActions">
          <ButtonGroup>
            <Button
              fullWidth
              component={Anchor}
              variant="basic"
              size="medium"
              href={this.getReservationsPath()}
            >
              {this.props.showBookingActions ? 'Manage reservation' : 'View details'}
            </Button>
          </ButtonGroup>
        </div>
      </div>
    );
  }
}
export default withRouter(BookingHistoryItem);
